import { useEffect, useRef, useState } from 'react';
import { Alert, Badge, ButtonIcon, FieldLabel, Text } from '@backstage/ui';
import { RiCheckLine, RiFileCopyLine } from '@remixicon/react';
import styles from './BuiSupport.module.css';

export function LoadingNotice({ label = 'Loading…' }: { label?: string }) {
  return (
    <div role="status" aria-live="polite" aria-atomic="true">
      <Alert isPending title={label} />
    </div>
  );
}

export function ErrorNotice({
  error,
  title = 'Unable to load this content',
}: {
  error: Error;
  title?: string;
}) {
  return (
    <div role="alert" aria-atomic="true">
      <Alert status="danger" icon title={title} description={error.message} />
    </div>
  );
}

export function StatusTag({ status }: { status: string }) {
  return (
    <Badge
      size="small"
      className={styles.statusTag}
      data-status={status.toLowerCase()}
    >
      {status}
    </Badge>
  );
}

export function CodeBlock({
  text,
  language,
}: {
  text: string;
  language: string;
}) {
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState(false);
  const resetTimer = useRef<number>();

  useEffect(
    () => () => {
      if (resetTimer.current) window.clearTimeout(resetTimer.current);
    },
    [],
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setCopyError(false);
    } catch {
      setCopied(false);
      setCopyError(true);
    }
    if (resetTimer.current) window.clearTimeout(resetTimer.current);
    resetTimer.current = window.setTimeout(() => {
      setCopied(false);
      setCopyError(false);
    }, 2400);
  };

  return (
    <div className={styles.codeBlock}>
      <div className={styles.codeToolbar}>
        <Text variant="body-x-small" color="secondary">
          {language}
        </Text>
        <ButtonIcon
          variant="tertiary"
          size="small"
          aria-label={copied ? 'Copied' : 'Copy code'}
          icon={copied ? <RiCheckLine /> : <RiFileCopyLine />}
          onPress={() => void copy()}
        />
      </div>
      <pre className={styles.codePre}>
        <code>{text}</code>
      </pre>
      <span className={styles.srOnly} role="status" aria-live="polite">
        {copied ? 'Code copied to clipboard.' : ''}
        {copyError ? 'Could not copy code to the clipboard.' : ''}
      </span>
    </div>
  );
}

export function NativeDateTimeField({
  id,
  label,
  description,
  errorMessage,
  value,
  isRequired,
  isDisabled,
  onChange,
}: {
  id: string;
  label: string;
  description?: string;
  errorMessage?: string;
  value: string;
  isRequired?: boolean;
  isDisabled?: boolean;
  onChange(value: string): void;
}) {
  const descriptionId = description ? `${id}-description` : undefined;
  const errorId = errorMessage ? `${id}-error` : undefined;

  return (
    <div className={styles.nativeField}>
      <FieldLabel
        label={label}
        description={description}
        htmlFor={id}
        descriptionId={descriptionId}
      />
      <input
        id={id}
        type="datetime-local"
        value={value}
        disabled={isDisabled}
        required={isRequired}
        aria-invalid={errorMessage ? 'true' : undefined}
        aria-describedby={
          [descriptionId, errorId].filter(Boolean).join(' ') || undefined
        }
        className={styles.nativeInput}
        onChange={event => onChange(event.currentTarget.value)}
      />
      {errorMessage && (
        <Text id={errorId} as="p" variant="body-small" color="danger">
          {errorMessage}
        </Text>
      )}
    </div>
  );
}
